'use client';

import { useEffect, useState } from 'react';

export type ConnectionQuality = 'offline' | 'slow' | 'medium' | 'fast' | 'unknown';

export type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | '4g';

export interface ConnectionState {
  quality: ConnectionQuality;
  saveData: boolean;
  isOnline: boolean;
  effectiveType?: EffectiveConnectionType;
  downlinkMbps?: number;
  rttMs?: number;
}

interface NetworkInformationLike extends EventTarget {
  effectiveType?: EffectiveConnectionType;
  saveData?: boolean;
  downlink?: number;
  rtt?: number;
}

const EFFECTIVE_TYPE_TO_QUALITY: Record<EffectiveConnectionType, ConnectionQuality> = {
  'slow-2g': 'slow',
  '2g': 'slow',
  '3g': 'medium',
  '4g': 'fast',
};

const INITIAL_STATE: ConnectionState = {
  quality: 'unknown',
  saveData: false,
  isOnline: true,
};

function getConnection(): NetworkInformationLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}

function readState(connection: NetworkInformationLike | null): ConnectionState {
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  if (!isOnline) {
    return { quality: 'offline', saveData: false, isOnline: false };
  }
  if (!connection) {
    return { quality: 'unknown', saveData: false, isOnline: true };
  }

  const saveData = connection.saveData === true;
  const effectiveType = connection.effectiveType;
  const mappedQuality: ConnectionQuality = effectiveType
    ? EFFECTIVE_TYPE_TO_QUALITY[effectiveType]
    : 'unknown';
  const quality: ConnectionQuality = saveData ? 'slow' : mappedQuality;

  return {
    quality,
    saveData,
    isOnline: true,
    ...(effectiveType !== undefined ? { effectiveType } : {}),
    ...(connection.downlink !== undefined ? { downlinkMbps: connection.downlink } : {}),
    ...(connection.rtt !== undefined ? { rttMs: connection.rtt } : {}),
  };
}

function isSameState(a: ConnectionState, b: ConnectionState): boolean {
  return (
    a.quality === b.quality &&
    a.saveData === b.saveData &&
    a.isOnline === b.isOnline &&
    a.effectiveType === b.effectiveType &&
    a.downlinkMbps === b.downlinkMbps &&
    a.rttMs === b.rttMs
  );
}

/**
 * One-shot synchronous read of the connection state.
 *
 * Use from inside effects that need the value at init time (e.g. picking a
 * Shaka `defaultBandwidthEstimate` once at player setup). The hook subscribes
 * to updates and is the right primitive for live adaptation; this is the
 * non-reactive variant for init-time decisions.
 */
export function readConnectionStateSync(): ConnectionState {
  return readState(getConnection());
}

/**
 * Observes the user's network connection quality via the Network Information API.
 *
 * SSR-safe: returns `'unknown'` defaults on the server and on browsers without
 * `navigator.connection` (Safari, Firefox), so adaptation logic must treat
 * `'unknown'` as "assume fast" and never as "slow".
 */
export function useConnectionQuality(): ConnectionState {
  const [state, setState] = useState<ConnectionState>(INITIAL_STATE);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const connection = getConnection();

    const update = () => {
      const next = readState(connection);
      setState((prev) => (isSameState(prev, next) ? prev : next));
    };

    update();
    connection?.addEventListener('change', update);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);

    return () => {
      connection?.removeEventListener('change', update);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return state;
}
